// src/components/digital_twin/twin_engine/ui/HealthTab.js
// ML health: score history, model forecast, failure probability / RUL, contributing factors and
// reliability KPIs. Values are shown exactly as the models return them; missing → "—".
import React, { memo, useMemo } from 'react';
import { SimpleGrid, Text, VStack, HStack, Box } from '@chakra-ui/react';
import { FaHeartbeat, FaChartLine, FaTachometerAlt } from 'react-icons/fa';
import { Empty, KV, Section, Stat, TrendChart, fmtDateTime, useTwinTokens } from './ui';

const BANDS = [
  { from: 0, to: 40, color: '#E53E3E' },
  { from: 40, to: 60, color: '#DD6B20' },
  { from: 60, to: 80, color: '#D69E2E' },
];

function HealthTab({ health, sources, profile }) {
  const t = useTwinTokens();
  const history = sources.history?.data || [];
  const kpis = sources.kpis?.data || null;
  const forecast = useMemo(() => {
    const f = health?.forecast || [];
    const now = Date.now();
    return f.map((p) => ({ ts: now + p.hours * 3600000, value: p.value }));
  }, [health]);

  if (!health && !history.length && !kpis) {
    return (
      <Empty icon={FaHeartbeat} title="The ML model has not scored this asset yet">
        {sources.health?.status === 'error' ? `Predictive maintenance service error: ${sources.health.error}` : `Health scores appear once the predictive-maintenance model has run for this ${profile.label.toLowerCase()} (it needs sensor or inspection data).`}
      </Empty>
    );
  }
  return (
    <VStack align="stretch" spacing={4}>
      <Section title="ML health model" icon={FaHeartbeat} accent
        right={health?.modelVersion ? <Text fontSize="11px" color={t.faint}>model {health.modelVersion}</Text> : null}>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <Stat label="Health score" value={health?.score != null ? Math.round(health.score) : '—'} sub={health?.status || ''} />
          <Stat label="Trend" value={health?.trend || '—'} />
          <Stat label="Failure prob." value={health?.failureProbability != null ? `${Math.round(health.failureProbability * 100)}%` : '—'} />
          <Stat label="Remaining life" value={health?.rulDays != null ? `${Math.round(health.rulDays)} d` : '—'} sub={health?.ttfHours != null ? `TTF ${Math.round(health.ttfHours)} h` : ''} />
          {health?.degradationRate != null && <Stat label="Degradation" value={health.degradationRate} sub="per day" />}
          {health?.confidence != null && <Stat label="Confidence" value={`${Math.round(health.confidence * 100)}%`} />}
        </SimpleGrid>
        {health?.updatedAt && <Text fontSize="11px" color={t.faint} mt={2}>Scored {fmtDateTime(health.updatedAt)}</Text>}
        {health?.drivers?.length > 0 && (
          <Box mt={3}>
            <Text fontSize="11px" fontWeight="700" color={t.muted} mb={1}>CONTRIBUTING FACTORS</Text>
            {health.drivers.map((d, i) => {
              const label = typeof d === 'string' ? d : d.label || d.feature || d.name;
              const w = typeof d === 'object' ? d.weight ?? d.importance : null;
              return <KV key={`${label}-${i}`} label={label} value={w != null ? Number(w).toFixed(2) : ''} />;
            })}
          </Box>
        )}
      </Section>

      <Section title="Health history" icon={FaChartLine} right={<Text fontSize="11px" color={t.faint}>{history.length} records</Text>}>
        {history.length > 1
          ? <TrendChart points={history} domain={[0, 100]} bands={BANDS} label="ML health score history" format={(v) => Math.round(v)} />
          : <Text fontSize="13px" color={t.muted}>{history.length === 1 ? 'Only one score recorded so far.' : 'No health history recorded.'}</Text>}
        {forecast.length > 1 && (
          <Box mt={4}>
            <Text fontSize="11px" fontWeight="700" color={t.muted} mb={1}>MODEL FORECAST</Text>
            <TrendChart points={forecast} domain={[0, 100]} bands={BANDS} label="Forecast health" color="#805AD5" format={(v) => Math.round(v)} />
          </Box>
        )}
      </Section>

      <Section title="Reliability KPIs" icon={FaTachometerAlt}>
        {kpis ? (
          <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
            <Stat label="Availability" value={kpis.availability != null ? `${kpis.availability}%` : '—'} />
            <Stat label="OEE" value={kpis.oee != null ? `${kpis.oee}%` : '—'} />
            <Stat label="MTBF" value={kpis.mtbf != null ? `${kpis.mtbf} h` : '—'} />
            <Stat label="MTTR" value={kpis.mttr != null ? `${kpis.mttr} h` : '—'} />
            <Stat label="Failures" value={kpis.failures ?? '—'} />
            <Stat label="Downtime" value={kpis.downtime != null ? `${kpis.downtime} h` : '—'} />
          </SimpleGrid>
        ) : <HStack><Text fontSize="13px" color={t.muted}>{sources.kpis?.status === 'error' ? `KPI service error: ${sources.kpis.error}` : 'No work-order history to compute KPIs yet.'}</Text></HStack>}
      </Section>
    </VStack>
  );
}

export default memo(HealthTab);
